import { observable, makeObservable } from 'mobx'
import * as R from 'ramda'
import i18n from 'i18next'
import QListAPI from '../../QList/QListAPI'
import { getOptionTitle, getQuestionTitle } from '../../utils/QuestionHelper'
import { unescapeHtmlTags, stripOutHtml, hasRequiredDependencies } from '../helpers'

class DataExplorerStore {
    @observable reports = []

    @observable report = {
        title: i18n.t('navmenu.dataExplorer'),
    }

    @observable loading = true

    @observable loadingData = false

    @observable industry

    @observable productType

    @observable surveyType

    @observable respondentType

    @observable questions

    @observable chosenQuestionIds = []

    @observable reportData

    @observable showComparisonSelector = false

    @observable showProductSelector = false

    @observable collapseLeft = false

    @observable includedProducts = []

    @observable yearOptions = []

    @observable years = []

    @observable benchmarks = ['industry', 'client']

    @observable periods = [36]

    @observable filterTree = []

    @observable npsQuestions = []

    @observable currencyConversions = {}

    @observable selectedCurrency = 'USD'

    @observable showCurrency = false

    @observable rawData = {}

    benchmarkOptions = [
        { label: 'Industry', value: 'industry' },
        { label: 'Company', value: 'client' },
        { label: 'Division', value: 'division' },
        { label: 'Portfolio', value: 'portfolio' },
    ]

    periodOptions = [{ label: '36 Months', value: 36 }, { label: 'Long-term', value: 84 }]

    productMaps = []

    productNames

    portfolioNames

    allowedQuestions = ['RD', 'CH', 'TB', 'SB', 'OD', 'SG', 'NF', 'I', 'PC', 'FM']

    todaysCurrencyRates = {}

    questionMap = new Map()

    nonYears = {
        YOY: 'Year on year',
        24: '24 Months',
        36: '36 Months',
        84: 'Long-term',
        ALL: 'All time',
    }

    constructor(store) {
        makeObservable(this)
        this.appStore = store
        this.QListAPI = new QListAPI(store.http)
        this.init()
    }

    init = async () => {
        const [productMaps, products, portfolios, divisions] = await Promise.all([
            this.getProdMap(),
            this.getProducts(),
            this.getPortfolios(),
            this.getDivisions(),
        ])
        this.setBenchmarkOptions()
        this.getTodaysExchangeRates()

        this.portfolioNames = portfolios
        this.divisionNames = divisions
        this.productMaps = productMaps
        this.productNames = products
        this.productNames.industry = 'Industry'
        this.productNames.client = this.appStore.userData.clientName
        this.loading = false
        const possibleIndustries = Object.values(R.pathOr({}, ['productMaps'], this))
        if (possibleIndustries.length === 1) {
            this.updateIndustry(possibleIndustries[0])
        }
    }

    setBenchmarkOptions = () => {
        const { appStore } = this
        const { userData } = appStore
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        const benchmarks = userData.benchmarks
            ? Object.keys(userData.benchmarks).filter((b) => userData.benchmarks[b])
                .map((b) => (b === 'company' ? 'client' : b))
            : []
        this.benchmarkOptions = this.benchmarkOptions.filter((b) => (
            benchmarks.includes(b.value) && b.value === 'division' ? isMultiDivision : benchmarks.includes(b.value)
        ))
        this.benchmarks = this.benchmarks.filter((b) => benchmarks.includes(b))
    }

    getTodaysExchangeRates = async () => {
        const { appStore } = this
        let exchangeRates = {}

        try {
            exchangeRates = await appStore.http.get('/currency/dayrate')
        } catch (e) {
            console.error(e)
        }
        this.todaysCurrencyRates = R.pathOr({}, ['rates'], exchangeRates)
    }

    getData = async (useNewEndpoint) => {
        const { appStore } = this
        this.loadingData = true
        this.showCurrency = false
        const body = {
            widget: [],
        }

        body.widget.push({
            responseType: 'DataExplorer',
            industry: this.industry,
            productType: this.productType,
            surveyType: this.surveyType,
            respondentType: this.respondentType,
            questionId: '00000000-0000-0000-0000-000000000000',
            clientId: this.appStore.userData.clientId,
            scope: 'product',
            dataExplorer: {
                rows: this.chosenQuestionIds.slice().map((q) => {
                    const [id, formId] = q.split('_')
                    const question = this.questionMap.get(q.toUpperCase())
                    if (formId === 'KPI') {
                        return { id: '00000000-0000-0000-0000-000000000000', kpi: id }
                    }
                    return formId
                        ? {
                            id: id.toUpperCase(),
                            formId: formId.toUpperCase(),
                            parentId: question.parentId,
                        }
                        : { id: id.toUpperCase(), parentId: question.parentId }
                }),
                benchmarks: ['portfolio', 'division', 'industry', 'client'],
            },
        })

        const data = await appStore.http.post(process.env.USE_NEW_DASHBOARD_ENDPOINT && useNewEndpoint ? '/report/v1/dashboard' : '/v2/dashboard', JSON.stringify(body))

        this.currencyConversions = R.pathOr({}, ['exchangeRates'], data)

        this.rawData = data

        this.setReportData()
        this.loadingData = false
    }

    setReportData = () => {
        const { rawData } = this
        let responseIndex = 0
        this.reportData = this.chosenQuestionIds.slice().map((questionIds) => {
            const qId = questionIds.split('_')[0]
            const question = this.questions.find((q) => q.question.id === qId)
            const questionType = R.pathOr('', ['question', 'questionTypeCode'], question)
            if (questionType === 'TB') {
                return R.pathOr([], ['question', 'optionSetY', 'options'], question).map((o) => {
                    responseIndex += 1
                    return this.formatData(R.pathOr([], ['widget', 0, 'dataExplorer', responseIndex - 1], rawData), question, o)
                })
            }
            responseIndex += 1
            return this.formatData(R.pathOr([], ['widget', 0, 'dataExplorer', responseIndex - 1], rawData), question)
        }).flat()
    }

    toggleComparisonSelector = () => {
        this.showComparisonSelector = !this.showComparisonSelector
    }

    toggleProductSelector = () => {
        this.showProductSelector = !this.showProductSelector
    }

    toggleCollapse = () => {
        this.collapseLeft = !this.collapseLeft
    }

    addChosenQuestion = (id, index) => {
        this.chosenQuestionIds.splice(index, 0, id)
    }

    removeChosenQuestion = (id) => {
        this.chosenQuestionIds = this.chosenQuestionIds.filter((q) => q !== id)
    }

    addAllQuestions = () => {
        this.chosenQuestionIds = this.questions
            .map(({ question }) => (
                question.formId ? `${question.id}_${question.formId}` : question.id
            ))
    }

    changeIncludedProducts = (products) => {
        this.includedProducts = products
    }

    changeIncludedPeriods = (periods) => {
        this.periods = periods
    }

    changeCurrency = ({ value }) => {
        this.selectedCurrency = value
        this.setReportData()
    }

    getCurrencySymbol = (locale = 'en-GB') => {
        let currencySymbol
        const { selectedCurrency } = this
        try {
            currencySymbol = (0).toLocaleString(
                locale,
                {
                    style: 'currency',
                    currency: selectedCurrency,
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                },
            ).replace(/\d/g, '').trim().slice(-1)
        } catch (e) {
            currencySymbol = selectedCurrency
        }
        return currencySymbol
    }

    getProducts = async () => {
        const { appStore } = this
        try {
            const products = await appStore.http.get('/product')
            this.products = products
            const uniqueProducts = new Set()
            const productNames = products.reduce((obj, p) => {
                uniqueProducts.add(String(p.productId))
                return ({ ...obj, [p.productId]: `${p.name}${p.edition ? ` - ${p.edition}` : ''}` })
            }, {})
            this.includedProducts = [...this.includedProducts, ...uniqueProducts]
            return productNames
        } catch (e) {
            this.products = []
            return {}
        }
    }

    getPortfolios = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const portfolios = role > 130 && role !== 150 ? [] : await appStore.http.get('/portfolio')
            this.portfolios = portfolios
            const uniquePortfolios = new Set()
            const portfolioNames = portfolios.reduce((obj, p) => {
                const portfolioKey = `portfolio_${p.portfolioId}`
                uniquePortfolios.add(portfolioKey)
                return { ...obj, [portfolioKey]: p.name }
            }, {})
            this.includedProducts = [...this.includedProducts, ...uniquePortfolios]
            return portfolioNames
        } catch (e) {
            this.portfolios = []
            return {}
        }
    }

    getDivisions = async () => {
        const { appStore } = this
        const { userData } = appStore
        const { role } = userData
        try {
            const divisions = role > 120 && role !== 150 ? [] : await appStore.http.get('/division')
            this.divisions = divisions
            const uniqueDivisions = new Set()
            const divisionNames = divisions.reduce((obj, p) => {
                const divisionKey = `division_${p.divisionId}`
                uniqueDivisions.add(divisionKey)
                return { ...obj, [divisionKey]: p.name }
            }, {})
            this.includedProducts = [...this.includedProducts, ...uniqueDivisions]
            return divisionNames
        } catch (e) {
            this.divisions = []
            return {}
        }
    }

    getProdMap = async () => {
        const { appStore } = this
        const { clientId } = appStore.userData
        const productMaps = await appStore.http.get(`/productmap/${clientId}`)
        const prodMap = productMaps.reduce((obj, m) => {
            let newObj = { ...obj }

            const industryLens = R.lensPath([m.industrycode])
            newObj = R.set(industryLens, R.or(R.view(industryLens, newObj),
                { label: m.industryname, value: m.industrycode, children: {} }), newObj)

            const productLens = R.lensPath([m.industrycode, 'children', m.producttypecode])
            newObj = R.set(productLens, R.or(R.view(productLens, newObj),
                { label: m.producttypename, value: m.producttypecode, children: {} }), newObj)

            const surveyLens = R.lensPath([m.industrycode, 'children', m.producttypecode, 'children', m.surveytypecode])
            newObj = R.set(surveyLens, R.or(R.view(surveyLens, newObj),
                { label: m.surveytypename, value: m.surveytypecode, children: {} }), newObj)

            const respondentLens = R.lensPath(
                [m.industrycode, 'children', m.producttypecode, 'children', m.surveytypecode, 'children', m.respondenttypecode],
            )
            newObj = R.set(respondentLens, R.or(R.view(respondentLens, newObj),
                { label: m.respondenttypename, value: m.respondenttypecode }), newObj)

            return newObj
        }, {})
        return prodMap
    }

    getProductSelectorData = () => {
        const {
            divisions, portfolios, products, appStore, includedProducts,
        } = this

        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        const portfolioObj = portfolios.reduce((po, p) => {
            po[p.portfolioId] = {
                ...p,
                label: p.name,
                value: `portfolio_${p.portfolioId}`,
                children: [],
                checked: includedProducts.includes(String(`portfolio_${p.portfolioId}`)),
            }
            return po
        }, {})

        products.forEach((p) => portfolioObj[p.portfolioId].children.push(
            {
                ...p,
                label: `${p.name}${p.edition ? ` - ${p.edition}` : ''}`,
                value: String(p.productId),
                checked: includedProducts.includes(String(p.productId)),
            },
        ))

        if (isMultiDivision && divisions && divisions.length) {
            const divisionObj = divisions.reduce((dObj, d) => {
                dObj[d.divisionId] = {
                    ...d,
                    label: d.name,
                    value: `division_${d.divisionId}`,
                    children: [],
                    checked: includedProducts.includes(String(`division_${d.divisionId}`)),
                }
                return dObj
            }, {})

            Object.values(portfolioObj).forEach((p) => divisionObj[p.divisionId].children.push(p))
            return Object.values(divisionObj)
        }
        return Object.values(portfolioObj)
    }

    createTreeData = () => {
        const { appStore } = this
        const defaultValues = this.includedProducts

        const groupedProducts = this.products.reduce((obj, p) => {
            obj[p.portfolioId] = obj[p.portfolioId] || []
            obj[p.portfolioId].push({
                ...p,
                value: String(p.productId),
                label: p.name,
                checked: defaultValues.includes(String(p.productId)),
            })
            return obj
        }, {})
        const groupedPortfolios = this.portfolios.reduce((obj, p) => {
            obj[p.divisionId] = obj[p.divisionId] || []
            obj[p.divisionId].push({
                ...p,
                children: groupedProducts[p.portfolioId],
                value: `portfolio_${p.portfolioId}`,
                label: p.name,
                checked: R.pathOr([], [p.portfolioId], groupedProducts).every((pr) => pr.checked),
            })
            return obj
        }, {})
        const groupedDivisions = this.divisions.reduce((arr, d) => {
            arr.push({
                ...d,
                children: groupedPortfolios[d.divisionId],
                value: `division_${d.divisionId}`,
                label: d.name,
                checked: R.pathOr([], [d.divisionId], groupedPortfolios).every((po) => po.checked),
            })
            return arr
        }, [])

        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)

        const filterTree = isMultiDivision ? groupedDivisions : Object.values(groupedPortfolios)[0]

        return filterTree
    }

    resetData = () => {
        this.chosenQuestionIds = []
        this.rawData = {}
        this.reportData = undefined
        this.yearOptions = []
        this.years = []
        this.setBenchmarkOptions()
        this.periods = [36]
        this.npsQuestions = []
    }

    updateIndustry = ({ value }) => {
        this.resetData()
        this.industry = value
        const possibleProductTypes = Object.values(
            R.pathOr(
                {},
                ['productMaps', value, 'children'],
                this,
            ),
        )
        if (possibleProductTypes.length === 1) {
            this.updateProductType(possibleProductTypes[0])
        }
    }

    updateProductType = ({ value }) => {
        this.resetData()
        this.productType = value
        const possibleSurveyTypes = Object.values(R.pathOr(
            {},
            ['productMaps', this.industry, 'children', value, 'children'],
            this,
        ))
        if (possibleSurveyTypes.length === 1) {
            this.updateSurveyType(possibleSurveyTypes[0])
        }
    }

    updateSurveyType = ({ value }) => {
        this.resetData()
        this.surveyType = value
        this.respondentType = undefined
        const possibleRespondentTypes = Object.values(R.pathOr(
            {},
            [
                'productMaps',
                this.industry,
                'children',
                this.productType,
                'children',
                value,
                'children',
            ],
            this,
        ))
        if (possibleRespondentTypes.length === 1) {
            this.updateRespondentType(possibleRespondentTypes[0])
        }
    }

    getKpis = async (questions) => {
        try {
            const kpis = await this.appStore.http.get('/kpi')
            const allowedKpiQuestions = questions.reduce((set, q) => (
                set.add(q.questionId.toLowerCase())
            ), new Set())
            const currencyQuestions = questions.reduce((set, q) => {
                if (R.pathOr(false, ['question', 'settings', 'isCurrency'], q)) {
                    set.add(q.questionId.toLowerCase())
                }
                return set
            }, new Set())
            return kpis.filter((k) => (
                k.surveyType === this.surveyType
                && k.respondentType === this.respondentType
                && k.productType === this.productType
                && k.industry === this.industry
            )).reduce(({ kpis, allowedKpis }, kpi) => {
                if (hasRequiredDependencies(kpi, allowedKpiQuestions, allowedKpis)) {
                    kpis.push({
                        ...kpi,
                        format: R.pathOr(null, ['settings', 'format'], kpi),
                        decimals: R.pathOr(null, ['settings', 'decimals'], kpi),
                        isCurrency: kpi.dependencies
                            .some((d) => currencyQuestions.has(d.id.toLowerCase())),
                    })
                    allowedKpis.add(kpi.kpiId.toLowerCase())
                }
                return {
                    kpis,
                    allowedKpis,
                }
            }, {
                kpis: [],
                allowedKpis: new Set(),
            }).kpis
        } catch (e) {
            return []
        }
    }

    updateRespondentType = async ({ value }) => {
        this.resetData()
        this.respondentType = value
        const questions = await this.getQlistQuestions()
        const kpis = await this.getKpis(questions)
        this.questions = [
            ...kpis.map((kpi) => (
                {
                    question: {
                        id: kpi.kpiId,
                        kpiName: kpi.name,
                        formId: 'KPI',
                        settings: {
                            isCurrency: kpi.isCurrency,
                            format: kpi.format,
                            decimals: kpi.decimals,
                        },
                    },
                    questionOid: kpi.clientId === 1 ? 1 : 3,
                }
            )),
            ...questions
                .filter(({ question }) => (
                    this.allowedQuestions.includes(question.questionTypeCode)
                )).map((q) => {
                    this.questionMap.set(`${q.questionId}`, q)
                    if (R.pathOr('', ['question', 'metadata', 'kpiType'], q) === 'NPS') this.npsQuestions.push(q.question.id)
                    if (q.question.questionTypeCode !== 'FM') return q
                    return q.question.optionSet
                        .filter((formQ) => ['SB', 'I'].includes(formQ.question.questionTypeCode))
                        .map((formQuestion) => {
                            this.questionMap.set(`${formQuestion.question.id.toUpperCase()}${q.questionId ? `_${q.questionId}` : ''}`, formQuestion)
                            return {
                                ...formQuestion,
                                questionOid: q.questionOid,
                                question: { ...formQuestion.question, formId: q.question.id },
                            }
                        })
                }).flat(),
        ]
    }

    getQlistQuestions = async () => {
        const {
            respondentType, productType, surveyType, appStore,
        } = this
        const includeExplori = surveyType === 'EM' ? '&includeExplori=true' : ''
        const url = `/qlist/getAllQlists2?productType=${productType}&surveyType=${surveyType}&respondentType=${respondentType}${includeExplori}`
        const allQuestions = await appStore.http.get(url)
        const questions = R.uniqBy(R.prop('questionId'), allQuestions)
        const uniqueQuestionIds = []
        return questions.reduce((qs, q) => {
            const question = JSON.parse(q.question)
            if (!uniqueQuestionIds.includes(q.questionId.toLowerCase())) {
                uniqueQuestionIds.push(q.questionId.toLowerCase())

                if (q.parentId) {
                    const parent = questions.find((ques) => ques.questionId === q.parentId)

                    if (parent) {
                        const parentQuestion = JSON.parse(parent.question)

                        if (parentQuestion.optionSet) {
                            const filteredParentOptions = R.pathOr([], ['optionSet', 'options'], parentQuestion)
                                .filter(({ isNonReporting }) => !isNonReporting)

                            if (question.optionSet) {
                                question.optionSet.options = [
                                    ...filteredParentOptions,
                                    ...question.optionSet.options,
                                ]
                            } else if (question.optionSetY) {
                                question.optionSetY.options = [
                                    ...filteredParentOptions,
                                    ...question.optionSetY.options,
                                ]
                            }
                        }
                    }
                }
                qs.push({ ...q, question, questionOid: R.pathOr(0, ['metadata', 'oid'], question) })
            }
            return qs
        }, [])
    }

    getQuestion = async (questionId, questionOid) => {
        const { appStore } = this
        const url = `/api/question/${questionId.toUpperCase()}/7`
        const question = await appStore.http.get(url)
        return { ...question, questionOid }
    }

    changeYears = (years) => {
        this.years = years
    }

    changeBenchmarks = (benchmarks) => {
        this.benchmarks = benchmarks
    }

    convertCurrency = (value, exchangeRateDate, isCurrency, formatDecimals) => {
        const { currencyConversions, todaysCurrencyRates, selectedCurrency } = this
        const decimals = !R.isNil(formatDecimals) ? formatDecimals : 2
        if (!isCurrency) return value.toFixed(decimals)
        const exchangeRate = R.pathOr(
            R.pathOr(1, [selectedCurrency], todaysCurrencyRates),
            [exchangeRateDate, selectedCurrency],
            currencyConversions,
        )
        return (value * exchangeRate).toFixed(decimals)
    }

    getTitle = (question) => {
        const { appStore } = this
        const { userData } = appStore
        const { locale } = userData
        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'shortTitle'], question)
        return R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale,
                defaultLocale: initialLocale,
                initialLocale: 'en_GB',
            },
        ))
    }

    formatData = (data, question, option) => {
        const { appStore } = this
        const { langcode } = appStore
        const questionName = question.question.kpiName || this.getTitle(question.question)

        const isCurrency = R.pathOr(false, ['question', 'settings', 'isCurrency'], question)
        const decimals = R.pathOr(null, ['question', 'settings', 'decimals'], question)
        const format = R.pathOr(null, ['question', 'settings', 'format'], question)
        const hasAverage = R.pathOr(null, ['hasAverage'], data)

        if (isCurrency) {
            this.showCurrency = true
        }

        const isNps = R.pathOr('', ['question', 'metadata', 'kpiType'], question) === 'NPS'
        const isKpi = R.pathOr('', ['question', 'formId'], question) === 'KPI'

        const optionName = option ? R.pathOr(
            '',
            ['text'],
            getOptionTitle({
                langs: option.langs,
                locale: langcode,
                defaultLocale: R.pathOr('en_GB', ['question', 'metadata', 'initialLocale'], question),
                initialLocale: 'en_GB',
            }),
        ) : ''

        const questionType = R.pathOr('', ['question', 'questionTypeCode'], question)
        const rows = {}
        const productGrouped = {}
        const portfoliosGrouped = {}
        data.yLabels.forEach(([key, value]) => { rows[key] = value })

        const allNames = { ...this.productNames, ...this.portfolioNames, ...this.divisionNames }
        const industryData = R.pathOr([], ['benchmarks', 'industry'], data).map((i) => ({ ...i, recordId: 'industry' }))
        const clientData = R.pathOr([], ['benchmarks', 'client'], data).map((i) => ({ ...i, recordId: 'client' }))
        const portfolios = R.pathOr([], ['benchmarks', 'portfolio'], data)
            .map((p) => ({ ...p, recordId: `portfolio_${p.recordId}` }))
            .filter((pd) => pd.recordId && allNames[pd.recordId])

        const products = [
            ...industryData,
            ...clientData,
            ...portfolios.sort((a, b) => allNames[a.recordId].localeCompare(allNames[b.recordId])),
            ...data.products
                .filter((pd) => pd.recordId && allNames[pd.recordId])
                .sort((a, b) => allNames[a.recordId].localeCompare(allNames[b.recordId])),
        ]
        const years = new Set(this.yearOptions.map((yo) => yo.value))

        products.forEach((p, i) => {
            if (!productGrouped[p.recordId]) {
                productGrouped[p.recordId] = {
                    name: p.recordId,
                    columns: [],
                    priority: i,
                }
            }

            if (p.year > 1900) years.add(p.year)

            productGrouped[p.recordId].columns.push({
                name: p.year,
                data: [...Object.keys(rows).map((k) => ({
                    name: unescapeHtmlTags(stripOutHtml(rows[k])),
                    value: this.convertCurrency(
                        R.pathOr(0, ['data', k], p),
                        R.pathOr(undefined, ['exhangeRateDate'], p),
                        isCurrency,
                        decimals,
                    ),
                })),
                {
                    name: isNps ? 'footerRow-Net Promoter Score ' : 'footerRow-Average',
                    value: this.convertCurrency(p.average || 0, p.exchangeRateDate, isCurrency, decimals),
                    hide: hasAverage === false,
                },
                {
                    name: 'footerRow-Total',
                    value: ['industry', 'client'].includes(p.recordId) ? '' : p.base,
                    hide: isKpi,
                },
                ],
            })
        })
        const sortedYears = [...years].sort((a, b) => b - a)
        this.years = sortedYears.slice(0, 2)
        this.yearOptions = [...years].map((y) => ({ label: y, value: y }))

        return {
            name: unescapeHtmlTags(stripOutHtml(`${questionName}${optionName ? ` - ${optionName}` : ''}`)),
            questionType,
            rows,
            isCurrency,
            format,
            decimals,
            parentId: question.parentId,
            products: Object.keys({ ...productGrouped, ...portfoliosGrouped })
                .map((k) => ({
                    name: {
                        ...this.productNames, ...this.portfolioNames, ...this.divisionNames,
                    }[k],
                    id: k,
                    columns: { ...productGrouped, ...portfoliosGrouped }[k].columns
                        .sort(({ name: a }, { name: b }) => a - b),
                    priority: { ...productGrouped, ...portfoliosGrouped }[k].priority,
                })).sort(({ priority: a }, { priority: b }) => a - b),
        }
    }

    filterReportData = () => {
        const {
            reportData, years, benchmarks, includedProducts, periods,
        } = this
        const portfolioEnabled = benchmarks.includes('portfolio')
        const divisionEnabled = benchmarks.includes('division')
        const allowedProducts = includedProducts.filter((p) => {
            if (p.includes('portfolio')) return portfolioEnabled
            if (p.includes('division')) return divisionEnabled
            return true
        })

        return reportData.map((q) => (
            {
                ...q,
                products: q.products.map((p) => (
                    {
                        ...p,
                        columns: p.columns.filter((c) => [...years, ...periods].includes((c.name))),
                    }
                )).filter((p) => (
                    p.columns
                    && p.columns.length
                    && [...benchmarks, ...allowedProducts].includes(p.id)
                )),
            }
        ))
    }

    getExcelFormat = (isPercentage, isCurrency) => {
        const { selectedCurrency } = this
        if (isPercentage) return '0.00%'
        if (!isCurrency) return '0.00'

        const currencySymbol = this.getCurrencySymbol(selectedCurrency)
        return `"${currencySymbol}"#,###.00`
    }

    getExportKind = (isPercentage, isCurrency) => {
        if (isCurrency) return 'Currency'
        if (isPercentage) return 'Pct'
        return 'Str'
    }

    excelExport = async () => {
        const { appStore } = this

        const convertValue = (value, pct) => (pct ? value / 100 : value)
        const data = this.filterReportData().map((table) => {
            const options = R.pathOr([], ['products', 0, 'columns', 0, 'data'], table)
                .filter((row) => !row.name.includes('footerRow-'))
                .map((row) => row.name)
            const averageValues = []
            const totalValues = []
            const allowPct = (table.format === '%' || ['RD', 'CH', 'TB', 'SB', 'OD', 'PC'].includes(table.questionType)) && !table.isCurrency

            return {
                title: table.name,
                description: '',
                kpi: '',
                questionType: '',
                chartType: '',
                currency: table.isCurrency
                    ? this.getCurrencySymbol(this.selectedCurrency)
                    : undefined,
                options,
                additional: [
                    {
                        title: table.questionType !== 'NF' ? 'Average' : 'Sum',
                        values: averageValues,
                        format: this.getExcelFormat(table.format === '%', table.isCurrency),
                        kind: this.getExportKind(table.format === '%', table.isCurrency),
                    },
                    {
                        title: 'Sample Size',
                        values: totalValues,
                        format: '0',
                    },
                ],
                subOptions: [
                    ['Years', ...table.products.map((p) => (
                        p.columns.map((y) => (this.nonYears[y.name] || String(y.name))))).flat()],
                ],
                columns: table.products.map((p) => (
                    p.columns.map((y) => ({
                        title: p.name,
                        plot: false,
                        kind: this.getExportKind(allowPct, table.isCurrency),
                        format: this.getExcelFormat(allowPct, table.isCurrency),
                        values: y.data.reduce((arr, v, index) => {
                            if (index === y.data.length - 2) {
                                averageValues.push(
                                    Number(v.value)
                                        ? convertValue(Number(v.value), table.format === '%')
                                        : null,
                                )
                                return arr
                            }
                            if (index === y.data.length - 1) {
                                totalValues.push(Number(v.value) ? Number(v.value) : null)
                                return arr
                            }
                            return [...arr, convertValue(Number(v.value), allowPct)]
                        }, []),
                    }))
                )).flat(),
            }
        })

        const downloadLink = await appStore.http.post('/export/excel', JSON.stringify({
            kind: 'Report',
            entrySheet: false,
            data,
        }))

        const link = document.createElement('a')
        link.href = downloadLink
        link.setAttribute(
            'download',
            true,
        )

        document.body.appendChild(link)
        link.click()
        link.parentNode.removeChild(link)
    }
}

export default DataExplorerStore
